<script>
  import { tick } from 'svelte';
  import handleFetch from '../../utils/handleFetch.ts';
  import handleSAASError from '../../utils/handleSAASError';
  import inputValidator from '../../utils/us-phone-format-validator';
  import { overpanelMap, closeOverpanel } from '../../stores/overpanelControl';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import OverpanelControl from '../OverpanelControl/OverpanelControl.svelte';
  import BaseSpinner from '../BaseSpinner/BaseSpinner.svelte';
  import { accessCode } from '../../stores/referrer';

  let {
    title,
    instructionLabelText,
    inputPlaceholder,
    inputValidationMessage,
    disclaimer,
    submitButtonText,
    successMessage,
    closeButtonText,
    errorMessage,
    errorInstruction,
    retryButtonText,
    retryLimit = 3,
    dataTestId = 'send-app-overpanel',
    lang,
    destination = $bindable(),
    closeCallback = () => {}
  } = $props();

  const overpanelKey = 'textApp';
  const inputId = `${overpanelKey}Input`;
  const formId = `${overpanelKey}Form`;
  const DisplayOptions = {
    form: 'form',
    success: 'success',
    error: 'error',
  };

  let inputNode = $state();
  let isLoading = $state(false);
  let isInputInvalid = $state(false);
  let shouldFocusInput = true;
  let shouldShowValidationMessage = $state(false);
  let shouldSuggestRetry = false;
  let display = $state(DisplayOptions.form);
  let retryCount = 0;
  let validateMessageShown = $derived(shouldShowValidationMessage && isInputInvalid);

  const reset = (des = '') => {
    destination = des;
    shouldShowValidationMessage = false;
    isLoading = false;
    shouldFocusInput = true;
    shouldSuggestRetry = false;
    display = DisplayOptions.form;
    retryCount = 0;
    validateMessageShown = shouldShowValidationMessage && isInputInvalid;
    closeCallback();
  };

  const handleInput = () => {
    const setValue = (value) => (inputNode.value = value);
    if (inputValidator) inputValidator(inputNode.value, setValue);

    isInputInvalid = !inputNode.checkValidity();
    if (!isInputInvalid) shouldShowValidationMessage = false;
  };

  const handleInputBlurred = () => {
    shouldShowValidationMessage = isInputInvalid;
    shouldFocusInput = false;
  };

  const handleInputFocused = () => {
    if (inputNode.checkValidity()) shouldShowValidationMessage = false;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (inputNode) destination = inputNode.value;

    isLoading = true;
    try {
      const response = await handleFetch(`${process.env.SAAS_APP_LINK_SMS_ENDPOINT}`, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': `${process.env.SAAS_API_KEY}`,
        },
        errorKey: 'Failed to send app via text.',
        body: JSON.stringify({
          phone_number: destination,
          locale: lang === 'es' ? 'es-US' : 'en-US',
          source: $accessCode,
        }),
      });

      if (response.status < 300 && response.status >= 200) {
        display = DisplayOptions.success;
      } else {
        const { id, message, statusCode } = await response.json();
        handleSAASError(`Get app via text request failed.`, id, {
          errorMessage: message,
          statusCode,
        });
        display = DisplayOptions.error;
        shouldSuggestRetry = true;
      }
    } catch (e) {
      console.error(e); /* eslint-disable-line no-console */ 
      display = DisplayOptions.error;
      shouldSuggestRetry = false;
    }

    isLoading = false;
    window.umami.track(`send-app-form-${DisplayOptions.success ? 'success' : 'failed'}`);
  };

  const handleCloseButtonClicked = () => {
    closeOverpanel(overpanelKey);
  };

  const handleRetryButtonClicked = () => {
    if (shouldSuggestRetry && retryCount < retryLimit) {
      retryCount++;
      handleFormSubmit({ target: { value: destination } });
    } else {
      reset(destination);
    }
  };

  $effect(() => {
    tick().then(() => {
      if (inputNode && shouldFocusInput) inputNode.focus();
		});
  });
</script>

{#if $overpanelMap.has(overpanelKey)}
  <OverpanelControl {title} {overpanelKey} closeCallback={reset}>
    <div class="flex flex-col min-h-full bg-gray-mercury" data-testid={dataTestId}>
      {#if display === DisplayOptions.form}
        <form
          id={formId}
          class="flex flex-col flex-1 px-4 py-6"
          onsubmit={handleFormSubmit}
          data-testid="{dataTestId}-form-section"
        >
          <label for={inputId} class="block text-base leading-loose font-semibold text-gray-gunmetal">
            {instructionLabelText}
          </label>
          <div class="relative">
            <input
              id={inputId}
              class="w-full rounded-sm border p-4 py-2 bg-white text-base leading-loose placeholder-gray-bluey focus:outline-hidden focus:shadow-focus"
              class:border-red={validateMessageShown}
              class:text-red={validateMessageShown}
              class:border-gray-gunmetal={!validateMessageShown}
              class:text-gray-gunmetal={!validateMessageShown}
              bind:this={inputNode}
              type="tel"
              pattern={'[0-9]{3}-[0-9]{3}-[0-9]{4}'}
              value={destination}
              oninput={handleInput}
              onblur={handleInputBlurred}
              onfocus={handleInputFocused}
              placeholder={inputPlaceholder}
              data-testid="{dataTestId}-input"
              required
            />
            <svg
              class="absolute top-1/2 -translate-y-1/2 right-0 w-4 h-4 mr-4 fill-current text-red"
              class:block={validateMessageShown}
              class:hidden={!validateMessageShown}
            >
              <use xlink:href={`${SPRITESHEET_URL}#alert`} />
            </svg>
          </div>
          <span
            class="text-sm text-red leading-loose"
            class:block={validateMessageShown}
            class:hidden={!validateMessageShown}
            data-testid="{dataTestId}-validation-message"
          >
            {inputValidationMessage}
          </span>
          <div class="markdown-wrapper mt-4 text-0.5sm leading-tight text-gray-charcoal">
            {@html disclaimer.content}
          </div>
        </form>
        <div class="flex justify-center lg:mb-4">
          <button
            form={formId}
            class="leading-loose text-center font-semibold bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
              focus:outline-hidden focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap min-w-25 px-8 flex
              items-center justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4 text-black"
            data-testid="{dataTestId}-submit-button"
          >
            {#if isLoading}
              <BaseSpinner
                className="w-6 h-6 my-2 mr-3 text-gray-opaque"
                dataTestId="{dataTestId}-form-section-spinner"
              />
            {:else}
              <!-- prettier-ignore -->
              {submitButtonText}
            {/if}
          </button>
        </div>
      {:else if display === DisplayOptions.success}
        <div class="flex flex-col flex-1 items-center justify-center p-4" data-testid="{dataTestId}-success-section">
          <svg class="w-22 h-22 mb-7 fill-current stroke-current text-blue-cerulean">
            <use xlink:href={`${SPRITESHEET_URL}#phone`} />
          </svg>
          <div class="markdown-wrapper mb-4 px-4 text-lg leading-tight text-center font-semibold text-blue-dark-navy">
            {@html successMessage.content}
          </div>
          <span class="text-lg leading-tight text-center font-semibold text-blue-cerulean">{destination}</span>
        </div>
        <div class="flex justify-center lg:mb-4">
          <button
            class="leading-loose text-center font-semibold bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
              focus:outline-hidden focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap min-w-25 px-8 flex
              items-center justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4 text-black"
            onclick={handleCloseButtonClicked}
            data-testid="{dataTestId}-close-button"
          >
            {closeButtonText}
          </button>
        </div>
      {:else if display === DisplayOptions.error}
        <div class="flex flex-col flex-1 items-center justify-center p-4" data-testid="{dataTestId}-error-section">
          <svg class="w-22 h-22 mb-8 fill-current text-red">
            <use xlink:href={`${SPRITESHEET_URL}#alert`} />
          </svg>
          <div class="markdown-wrapper mb-4 px-4 text-lg leading-tight text-center font-semibold text-blue-dark-navy">
            {@html errorMessage.content}
          </div>
          <span class="mb-8 text-lg leading-tight text-center font-semibold text-blue-cerulean">{destination}</span>
          <div class="markdown-wrapper px-4 leading-normal text-center text-blue-dark-navy">
            {@html errorInstruction.content}
          </div>
        </div>
        <div class="flex justify-center lg:mb-4">
          <button
            class="leading-loose text-center font-semibold bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
              focus:outline-hidden focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap min-w-25 px-8 flex
              items-center justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4"
            disabled={isLoading}
            class:italic={isLoading}
            class:text-gray-brownish={isLoading}
            class:bg-blue-very-light={isLoading}
            class:font-semibold={!isLoading}
            class:text-black={!isLoading}
            class:bg-blue-robin-egg={!isLoading}
            class:hover:bg-blue-light={!isLoading}
            onclick={isLoading ? null : handleRetryButtonClicked}
            data-testid="{dataTestId}-retry-button"
          >
            {#if isLoading}
              <BaseSpinner
                className="w-6 h-6 my-2 mr-3 text-gray-opaque"
                dataTestId="{dataTestId}-error-section-spinner"
              />
            {:else}
              <!-- prettier-ignore -->
              {retryButtonText}
            {/if}
          </button>
        </div>
      {/if}
    </div>
  </OverpanelControl>
{/if}
